import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Eye, Globe2, Inbox, Newspaper, Search, ShieldCheck, Sparkles, TrendingUp } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "../lib/api";
import BbiShell from "../components/BbiShell";

const fadeUp = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: "easeOut" },
};

/* ── Auto-rotating 3D-tilt hero carousel ── */
const HERO_IMAGES = [
  { src: "/bbi/hero-wall.jpg", alt: "BrandsByIndia brand wall — verified Indian brand tiles" },
  { src: "/bbi/rot-storefront.jpg", alt: "Indian brand storefront glowing with search visibility" },
  { src: "/bbi/rot-phone.jpg", alt: "Verified brand profile surrounded by AI search icons" },
  { src: "/bbi/rot-podium.jpg", alt: "Brands rising on the visibility leaderboard" },
];

function HeroCarousel() {
  const [i, setI] = useState(0);
  const [tilt, setTilt] = useState({ rx: 0, ry: 0 });

  useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % HERO_IMAGES.length), 9000);
    return () => clearInterval(t);
  }, []);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    setTilt({ rx: py * -10, ry: px * 12 });
  };

  return (
    <motion.div
      className="hidden lg:block"
      style={{ perspective: 1100 }}
      animate={{ y: [0, -12, 0] }}
      transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
      data-testid="bbi-hero-carousel"
    >
      <div
        onMouseMove={onMove}
        onMouseLeave={() => setTilt({ rx: 0, ry: 0 })}
        className="relative rounded-3xl overflow-hidden border border-white/10 shadow-2xl shadow-indigo-500/20 h-[380px]"
        style={{ transform: `rotateX(${tilt.rx}deg) rotateY(${tilt.ry}deg)`, transition: "transform 0.25s ease-out", transformStyle: "preserve-3d" }}
      >
        <AnimatePresence>
          <motion.img
            key={i}
            src={HERO_IMAGES[i].src}
            alt={HERO_IMAGES[i].alt}
            className="absolute inset-0 w-full h-full object-cover"
            initial={{ opacity: 0, scale: 1.08 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.4, ease: "easeOut" }}
          />
        </AnimatePresence>
        <div className="absolute inset-0 bg-gradient-to-t from-[#030712]/50 via-transparent to-transparent pointer-events-none" />
        <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2">
          {HERO_IMAGES.map((_, d) => (
            <button key={d} onClick={() => setI(d)} aria-label={`slide ${d + 1}`} data-testid={`hero-dot-${d}`}
              className={`h-1.5 rounded-full transition-all ${d === i ? "w-6 bg-white" : "w-1.5 bg-white/40 hover:bg-white/70"}`} />
          ))}
        </div>
      </div>
    </motion.div>
  );
}

/* ── Real-time activity pop (bottom-left) — built from LIVE platform data ── */
function LivePop({ brands, articles }) {
  const msgs = useMemo(() => {
    const out = [];
    brands.forEach((b) => {
      if (b.views > 0) out.push({ icon: Eye, text: `${b.name} crossed ${b.views} page views`, tone: "indigo" });
      out.push({ icon: Search, text: `${b.name} is being indexed for "${(b.keywords || b.category || "").split(",")[0].trim() || b.category}" searches`, tone: "emerald" });
      if ((b.boxes || 1) >= 3) out.push({ icon: Sparkles, text: `${b.name} upgraded to a Featured box`, tone: "amber" });
    });
    articles.forEach((a) => {
      out.push({ icon: Newspaper, text: `New AI article live for ${a.brand_name}: "${a.title.slice(0, 48)}…"`, tone: "fuchsia" });
      if (a.views > 0) out.push({ icon: TrendingUp, text: `${a.brand_name}'s article reached ${a.views} readers`, tone: "indigo" });
    });
    out.push({ icon: Inbox, text: "A customer enquiry was just delivered to a brand's inbox", tone: "emerald" });
    out.push({ icon: Globe2, text: "Sitemap updated & resubmitted to Google automatically", tone: "indigo" });
    return out.length ? out : [{ icon: Sparkles, text: "BrandsByIndia is live — first brands onboarding now", tone: "indigo" }];
  }, [brands, articles]);

  const [idx, setIdx] = useState(-1);
  useEffect(() => {
    const first = setTimeout(() => setIdx(0), 2000);
    const cycle = setInterval(() => setIdx((i) => (i + 1) % msgs.length), 6000);
    return () => { clearTimeout(first); clearInterval(cycle); };
  }, [msgs.length]);

  if (idx < 0 || !msgs.length) return null;
  const m = msgs[idx % msgs.length];
  const mins = (idx * 7 + 2) % 59 + 1;
  const tones = {
    indigo: "text-indigo-300 bg-indigo-500/15",
    fuchsia: "text-fuchsia-300 bg-fuchsia-500/15",
    emerald: "text-emerald-300 bg-emerald-500/15",
    amber: "text-amber-300 bg-amber-500/15",
  };
  return (
    <div key={idx} data-testid="bbi-live-pop"
      className="fixed bottom-5 left-5 z-40 max-w-xs bg-[#0B0F1E]/95 backdrop-blur-md border border-white/10 rounded-2xl p-4 shadow-2xl shadow-black/50"
      style={{ animation: "bbiPopIn 0.45s cubic-bezier(0.22,1,0.36,1)" }}>
      <style>{`@keyframes bbiPopIn { from { opacity:0; transform: translateY(16px) scale(0.96);} to { opacity:1; transform: translateY(0) scale(1);} }`}</style>
      <div className="flex items-start gap-3">
        <div className={`w-8 h-8 rounded-lg grid place-items-center shrink-0 ${tones[m.tone]}`}>
          <m.icon className="w-4 h-4" />
        </div>
        <div className="min-w-0">
          <div className="text-xs text-white leading-snug">{m.text}</div>
          <div className="text-[10px] text-slate-500 mt-1 flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> {mins} min ago · live on BrandsByIndia
          </div>
        </div>
      </div>
    </div>
  );
}

const POWERS = [
  { icon: ShieldCheck, t: "Verified Brand Page", d: "A dedicated, Google-indexed page with your story, logo and store links — with schema markup search engines understand." },
  { icon: Newspaper, t: "AI SEO Articles", d: "Keyword-rich buying guides written by AI about YOUR brand — published, indexed and linked to your stores every month." },
  { icon: Globe2, t: "Google + AI Visibility", d: "Sitemap, structured data and llms.txt so your brand surfaces on Google, ChatGPT, Gemini & Perplexity." },
  { icon: Inbox, t: "Customer Enquiries", d: "Visitors leave their phone & email on your page — delivered straight to your inbox and client portal." },
];

export default function BrandDirectory() {
  const [brands, setBrands] = useState([]);
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    document.title = "BrandsByIndia — Make Your Brand Impossible to Miss | Google & AI Visibility";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", "BrandsByIndia by EcomCatalog AI — verified brand pages, monthly AI SEO articles and measurable visibility on Google, ChatGPT & Gemini. ₹999 per box/month including GST.");
    api.get("/public/brands").then(({ data }) => setBrands(data.brands || [])).catch(() => {});
    api.get("/public/bbi/articles").then(({ data }) => setArticles(data.articles || [])).catch(() => {});

    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = "bbi-jsonld";
    ld.text = JSON.stringify([
      {
        "@context": "https://schema.org", "@type": "Service",
        name: "BrandsByIndia", serviceType: "Brand visibility & SEO service",
        provider: { "@type": "Organization", name: "EcomCatalog AI", url: "https://ecomcatalog.ai" },
        areaServed: "India", url: "https://ecomcatalog.ai/brands",
        description: "Verified brand pages, AI-written SEO articles and measurable Google + AI search visibility for Indian D2C brands.",
        offers: { "@type": "Offer", price: "999", priceCurrency: "INR",
          description: "₹999 per box per month including GST, auto-pay, cancel anytime" },
      },
      {
        "@context": "https://schema.org", "@type": "FAQPage",
        mainEntity: [
          { "@type": "Question", name: "What is BrandsByIndia?",
            acceptedAnswer: { "@type": "Answer", text: "BrandsByIndia is a brand visibility platform by EcomCatalog AI. Every Indian brand gets a verified public page, monthly AI-written SEO articles and structured data so the brand surfaces on Google, ChatGPT, Gemini and Perplexity. Plans start at ₹999 per box per month (including GST)." } },
          { "@type": "Question", name: "How does BrandsByIndia increase my brand's visibility on Google?",
            acceptedAnswer: { "@type": "Answer", text: "Your brand gets an indexed page with schema markup, fresh keyword-rich AI articles every month, automatic sitemap submission and machine-readable data. More quality indexed content means more brand searches won on Google." } },
          { "@type": "Question", name: "How do I join BrandsByIndia?",
            acceptedAnswer: { "@type": "Answer", text: "Pay online at ecomcatalog.ai/brands/pricing — ₹999 per box per month (including GST) via Razorpay Auto-Pay. Your verified page and client login ID & password are set up within 24 hours. More boxes give a bigger card, higher placement and more AI articles." } },
        ],
      },
    ]);
    document.head.appendChild(ld);
    return () => { document.getElementById("bbi-jsonld")?.remove(); };
  }, []);

  const totalViews = brands.reduce((s, b) => s + (b.views || 0), 0) + articles.reduce((s, a) => s + (a.views || 0), 0);

  return (
    <BbiShell>
      <LivePop brands={brands} articles={articles} />

      {/* ambient animated glow blobs */}
      <motion.div aria-hidden className="fixed -top-40 -right-40 w-[480px] h-[480px] rounded-full pointer-events-none z-0"
        style={{ background: "radial-gradient(circle, rgba(99,102,241,0.18), transparent 65%)" }}
        animate={{ scale: [1, 1.2, 1], opacity: [0.6, 1, 0.6] }} transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }} />
      <motion.div aria-hidden className="fixed bottom-0 -left-48 w-[520px] h-[520px] rounded-full pointer-events-none z-0"
        style={{ background: "radial-gradient(circle, rgba(217,70,239,0.12), transparent 65%)" }}
        animate={{ scale: [1.15, 1, 1.15], opacity: [0.5, 0.9, 0.5] }} transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }} />

      {/* HERO — pure BrandsByIndia power, no brand listings */}
      <section className="pt-16 sm:pt-24 pb-14 relative z-10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <motion.div initial={{ opacity: 0, y: 32 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: "easeOut" }}>
              <div className="text-[11px] uppercase tracking-[0.3em] text-indigo-400 font-bold flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5" /> BrandsByIndia
              </div>
              <h1 className="font-mkt-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tighter mt-4 leading-[1.05]">
                Make your brand <span className="mkt-gradient-text-vivid">impossible to miss.</span>
              </h1>
              <p className="text-base sm:text-lg text-slate-400 mt-5 max-w-xl leading-relaxed">
                A verified public page, monthly AI-written SEO articles and structured data —
                so your brand gets found on <span className="text-white font-semibold">Google, ChatGPT &amp; Gemini</span>,
                and customers reach you directly.
              </p>
              <div className="flex gap-3 mt-8 flex-wrap">
                <Link to="/brands/pricing" data-testid="bbi-home-cta-pricing"
                  className="mkt-btn-gradient text-sm">
                  Get Listed — ₹999/mo <ArrowRight size={14} />
                </Link>
                <Link to="/brands/all" data-testid="bbi-home-cta-wall"
                  className="px-6 py-3 rounded-full border border-white/15 text-sm font-semibold text-white hover:border-indigo-400/50 transition-colors flex items-center gap-2">
                  Explore the Brand Wall <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
              {totalViews > 0 && (
                <div className="flex items-center gap-2 mt-7 text-xs text-slate-500">
                  <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                  {totalViews}+ page views delivered to our brands — and counting
                </div>
              )}
            </motion.div>
            <HeroCarousel />
          </div>
        </div>
      </section>

      {/* THE POWER — what BBI does */}
      <section className="pb-16 relative z-10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <motion.h2 {...fadeUp} className="font-mkt-display text-3xl sm:text-4xl font-semibold tracking-tighter text-white">
            The power behind <span className="mkt-gradient-text-vivid">every box.</span>
          </motion.h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-9" data-testid="bbi-what-we-do">
            {POWERS.map((p, pi) => (
              <motion.div key={p.t}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, delay: pi * 0.12, ease: "easeOut" }}
                whileHover={{ y: -8, scale: 1.02 }}
                className="bg-white/[0.03] border border-white/10 rounded-2xl p-6 hover:border-indigo-400/40 hover:shadow-xl hover:shadow-indigo-500/10 transition-colors">
                <motion.div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-fuchsia-500 grid place-items-center mb-4"
                  animate={{ rotate: [0, 4, -4, 0] }} transition={{ duration: 5, repeat: Infinity, delay: pi * 0.7 }}>
                  <p.icon className="w-4 h-4 text-white" />
                </motion.div>
                <div className="text-white font-semibold text-sm">{p.t}</div>
                <p className="text-xs text-slate-400 mt-2 leading-relaxed">{p.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT HELPS — image + growth story */}
      <section className="pb-16 relative z-10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <motion.div {...fadeUp} className="bg-gradient-to-br from-indigo-500/10 via-transparent to-fuchsia-500/10 border border-indigo-400/20 rounded-3xl p-8 sm:p-12 grid lg:grid-cols-[320px,1fr] gap-10 items-center">
            <motion.img src="/bbi/growth.jpg" alt="Brand visibility growth on Google and AI search" loading="lazy"
              className="hidden lg:block w-full rounded-2xl border border-white/10 shadow-xl shadow-fuchsia-500/10"
              animate={{ y: [0, -10, 0] }} transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              data-testid="bbi-growth-image" />
            <div>
              <h2 className="font-mkt-display text-3xl sm:text-4xl font-semibold tracking-tighter text-white">
                From invisible to <span className="mkt-gradient-text-vivid">in-demand.</span>
              </h2>
              <div className="mt-6 space-y-4">
                {[
                  ["Week 1", "Your verified page goes live, indexed by Google with schema markup — your brand becomes officially searchable."],
                  ["Month 1", "AI articles start targeting the exact searches your customers type — each one links back to your stores."],
                  ["Month 2-3", "Brand-name searches start landing on YOUR page. Visitors leave enquiries that reach your email instantly."],
                  ["Ongoing", "Every new article compounds your presence — on Google, and in answers from ChatGPT, Gemini & Perplexity."],
                ].map(([w, d]) => (
                  <div key={w} className="flex gap-4">
                    <div className="text-[11px] uppercase tracking-wider text-indigo-400 font-bold w-20 shrink-0 pt-0.5">{w}</div>
                    <p className="text-sm text-slate-300 leading-relaxed">{d}</p>
                  </div>
                ))}
              </div>
              <Link to="/brands/how-it-works" className="inline-flex items-center gap-2 text-sm text-indigo-400 hover:text-indigo-300 font-semibold mt-7" data-testid="bbi-home-how-link">
                See the full process <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* LATEST ARTICLES */}
      {articles.length > 0 && (
        <section className="pb-16 relative z-10" data-testid="bbi-articles-section">
          <div className="max-w-7xl mx-auto px-5 sm:px-8">
            <motion.h2 {...fadeUp} className="text-white font-semibold text-2xl tracking-tight">Latest brand articles</motion.h2>
            <p className="text-sm text-slate-400 mt-2">Fresh AI-written guides published for our brands — this is the content that gets them found.</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-7">
              {articles.slice(0, 6).map((a, ai) => (
                <motion.div key={`${a.brand_slug}-${a.slug}`}
                  initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.5, delay: ai * 0.1 }}
                  whileHover={{ y: -6 }}>
                <Link to={`/brands/${a.brand_slug}/${a.slug}`}
                  data-testid={`bbi-article-${a.slug}`}
                  className="group block h-full bg-white/[0.03] border border-white/10 rounded-2xl p-6 hover:border-indigo-400/40 transition-all">
                  <div className="text-[11px] uppercase tracking-wider text-indigo-400 font-bold">{a.brand_name}</div>
                  <div className="text-white font-semibold mt-2 leading-snug line-clamp-2 group-hover:text-indigo-300 transition-colors">{a.title}</div>
                  {a.meta_description && <p className="text-xs text-slate-400 mt-2.5 line-clamp-2">{a.meta_description}</p>}
                  <div className="flex items-center justify-between mt-4 text-xs text-slate-500">
                    <span>{(a.published_at || "").slice(0, 10)}</span>
                    <span className="text-indigo-400 font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">Read <ArrowRight className="w-3 h-3" /></span>
                  </div>
                </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FINAL CTA */}
      <section className="pb-24 relative z-10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <motion.div {...fadeUp} className="bg-gradient-to-r from-indigo-500/10 to-fuchsia-500/10 border border-indigo-400/20 rounded-2xl p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
            <div>
              <div className="text-white font-semibold text-lg">Want your brand on BrandsByIndia?</div>
              <p className="text-sm text-slate-400 mt-1">Verified brand page + AI SEO articles + customer enquiries — ₹999/month incl. GST, live in 24 hours.</p>
            </div>
            <Link
              to="/brands/pricing"
              data-testid="brand-directory-cta"
              className="px-6 py-3 rounded-full bg-indigo-500 hover:bg-indigo-400 text-sm font-semibold transition-colors whitespace-nowrap"
              style={{ color: "#FFFFFF" }}
            >
              Get Listed — ₹999/mo
            </Link>
          </motion.div>
        </div>
      </section>
    </BbiShell>
  );
}
